import { useMeterStatusThresholds } from "@/shared/hooks/useMeterStatusThresholds";
import {
	METER_STATUS_LABELS,
	type MeterStatusLevel,
	resolveMeterStatusBand,
} from "@/shared/utils/meterStatusLevels";
import { getMomentTz } from "@dexteel/mesf-core";
import {
	Box,
	Checkbox,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import {
	createMeterNameResolver,
	type UtilityConsumptionMeter,
	type UtilityConsumptionMeterStatus,
} from "../../shared/consumption/models/UtilityConsumption";
import {
	formatVolume,
	NO_DATA_TEXT,
	VOLUME_UNIT,
} from "../../shared/consumption/utils/format";
import {
	CONSUMPTION_CAPTION_SX,
	CONSUMPTION_LABEL_SX,
	CONSUMPTION_TABULAR_SX,
} from "../../shared/consumption/utils/tokens";

const moment = getMomentTz();

const SectionRow = ({ label, count }: { label: string; count: number }) => (
	<TableRow>
		<TableCell
			colSpan={8}
			sx={{
				bgcolor: "#F4F6F8",
				borderBottomColor: "divider",
				color: "#5B6670",
				fontSize: "0.7rem",
				fontWeight: 700,
				letterSpacing: "0.04em",
				py: 0.25,
				textTransform: "uppercase",
			}}
		>
			{label} · {count}
		</TableCell>
	</TableRow>
);

const formatLag = (hours: number) => {
	if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min`;
	if (hours < 48) return `${Math.round(hours)} h`;
	return `${Math.round(hours / 24)} d`;
};

const headCellSx = {
	...CONSUMPTION_LABEL_SX,
	borderBottomColor: "divider",
	fontWeight: 700,
	py: 0.5,
	whiteSpace: "nowrap",
} as const;
const cellSx = {
	fontSize: "0.75rem",
	py: 0.5,
	whiteSpace: "nowrap",
} as const;

interface MeterStatusTableProps {
	meters: UtilityConsumptionMeter[];
	statuses: UtilityConsumptionMeterStatus[];
	selectedMeterIds: string[] | null;
	onMeterChange: (meterIds: string[] | null) => void;
	onMeterHover: (meterId: string | null) => void;
}

export const MeterStatusTable = ({
	meters,
	statuses,
	selectedMeterIds,
	onMeterChange,
	onMeterHover,
}: MeterStatusTableProps) => {
	const { drafts: thresholds } = useMeterStatusThresholds();
	const getMeterName = createMeterNameResolver(meters);
	const statusByMeterId = new Map(
		statuses.map((status) => [status.ExternalMeterId, status]),
	);
	const selectedIds =
		selectedMeterIds === null ? null : new Set(selectedMeterIds);
	const isSelected = (meterId: string) =>
		selectedIds === null || selectedIds.has(meterId);

	const allMeterIds = meters.map((meter) => meter.ExternalMeterId);
	const commit = (nextIds: Set<string>) => {
		const orderedIds = allMeterIds.filter((meterId) => nextIds.has(meterId));
		onMeterChange(orderedIds.length === allMeterIds.length ? null : orderedIds);
	};

	const toggleMeter = (meterId: string) => {
		const nextIds = new Set(allMeterIds.filter(isSelected));
		if (nextIds.has(meterId)) nextIds.delete(meterId);
		else nextIds.add(meterId);
		commit(nextIds);
	};

	const rows = [
		...meters.filter((meter) => isSelected(meter.ExternalMeterId)),
		...meters.filter((meter) => !isSelected(meter.ExternalMeterId)),
	].map((meter) => {
		const meterId = meter.ExternalMeterId;
		const onScreen = isSelected(meterId);
		const status = statusByMeterId.get(meterId);
		const latestReadUtc = status?.LatestReadTimeUtc ?? null;
		const lagHours =
			latestReadUtc === null
				? null
				: (Date.now() - latestReadUtc.getTime()) / 3_600_000;

		const gallons = status?.PeriodGallons ?? null;
		const coverage =
			status === undefined || status.PeriodExpectedReadings === 0
				? null
				: (status.PeriodReadings / status.PeriodExpectedReadings) * 100;

		const state = resolveMeterStatusBand(
			thresholds,
			"Lag",
			lagHours ?? Number.POSITIVE_INFINITY,
		);
		const coverageBand =
			coverage === null
				? null
				: resolveMeterStatusBand(thresholds, "Coverage", coverage);

		return {
			meterId,
			name: getMeterName(meterId),
			accountNumber: meter.AccountNumber,
			color: meter.Color,
			onScreen,
			latestReadUtc,
			lagHours,
			coverage,
			coverageBand,
			gallons,
			state,
		};
	});

	const selectedRows = rows.filter((row) => row.onScreen);
	const otherRows = rows.filter((row) => !row.onScreen);
	const isSplit = selectedRows.length > 0 && otherRows.length > 0;

	const countOf = (...levels: MeterStatusLevel[]) =>
		rows.filter((row) => levels.includes(row.state.Level)).length;
	const headline = [
		`${countOf("Reporting")} reporting`,
		`${countOf("Late")} late`,
		`${countOf("NoData")} stale`,
	].join(" · ");

	const renderRow = (row: (typeof rows)[number]) => (
		<TableRow
			key={row.meterId}
			hover
			onMouseEnter={() => onMeterHover(row.onScreen ? row.meterId : null)}
			onMouseLeave={() => onMeterHover(null)}
			onFocus={() => onMeterHover(row.onScreen ? row.meterId : null)}
			onBlur={() => onMeterHover(null)}
		>
			<TableCell padding="checkbox" sx={{ ...cellSx, width: 36 }}>
				<Checkbox
					size="small"
					checked={row.onScreen}
					onChange={() => toggleMeter(row.meterId)}
					sx={{ p: 0.25 }}
				/>
			</TableCell>
			<TableCell sx={{ ...cellSx, fontWeight: 600 }}>
				<Box sx={{ alignItems: "center", display: "flex", gap: 0.75 }}>
					<Box
						sx={{
							bgcolor: row.color,
							borderRadius: "2px",
							flexShrink: 0,
							height: 10,
							width: 10,
						}}
					/>
					{row.name}
				</Box>
			</TableCell>
			<TableCell sx={{ ...cellSx, color: "#5B6670" }}>
				{row.accountNumber ?? NO_DATA_TEXT}
			</TableCell>
			<TableCell sx={{ ...cellSx, ...CONSUMPTION_TABULAR_SX }}>
				{row.latestReadUtc === null
					? NO_DATA_TEXT
					: moment(row.latestReadUtc).format("M/D HH:mm")}
			</TableCell>
			<TableCell
				align="right"
				sx={{
					...cellSx,
					...CONSUMPTION_TABULAR_SX,
					color: row.state.Color,
					fontWeight: 600,
				}}
			>
				{row.lagHours === null ? NO_DATA_TEXT : formatLag(row.lagHours)}
			</TableCell>
			<TableCell sx={cellSx}>
				<Tooltip
					title={
						row.state.Level === "Reporting"
							? "Reporting on time."
							: `No reading for ${row.state.Threshold} hours or more.`
					}
				>
					<Box
						component="span"
						sx={{
							bgcolor: row.state.BackgroundColor,
							borderRadius: 1,
							color: row.state.Color,
							display: "inline-flex",
							fontSize: "0.7rem",
							fontWeight: 600,
							justifyContent: "center",
							minWidth: "11ch",
							px: 0.75,
							py: 0.25,
						}}
					>
						{METER_STATUS_LABELS[row.state.Level]}
					</Box>
				</Tooltip>
			</TableCell>
			<TableCell sx={cellSx}>
				{row.coverage === null || row.coverageBand === null ? (
					<Box component="span" sx={{ color: "#5B6670" }}>
						{NO_DATA_TEXT}
					</Box>
				) : (
					<Box sx={{ alignItems: "center", display: "flex", gap: 0.75 }}>
						<Box
							sx={{
								bgcolor: row.coverageBand.BackgroundColor,
								borderRadius: 1,
								flex: 1,
								height: 6,
								minWidth: 48,
								overflow: "hidden",
							}}
						>
							<Box
								sx={{
									bgcolor: row.coverageBand.Color,
									height: "100%",
									width: `${Math.min(100, row.coverage)}%`,
								}}
							/>
						</Box>
						<Box
							component="span"
							sx={{ ...CONSUMPTION_TABULAR_SX, color: "#5B6670" }}
						>
							{Math.round(row.coverage)}%
						</Box>
					</Box>
				)}
			</TableCell>
			<TableCell align="right" sx={{ ...cellSx, ...CONSUMPTION_TABULAR_SX }}>
				{formatVolume(row.gallons) ?? NO_DATA_TEXT}
			</TableCell>
		</TableRow>
	);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: 0,
				minWidth: 0,
			}}
		>
			<Box
				sx={{
					alignItems: "baseline",
					display: "flex",
					flexWrap: "wrap",
					gap: 1,
					justifyContent: "space-between",
					pb: 0.5,
				}}
			>
				<Typography variant="subtitle1" fontWeight={600}>
					Meters
				</Typography>
				<Typography component="div" sx={CONSUMPTION_CAPTION_SX}>
					{headline}
				</Typography>
			</Box>
			<Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
				<Table size="small" stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell padding="checkbox" sx={{ ...headCellSx, width: 36 }}>
								<Checkbox
									size="small"
									checked={otherRows.length === 0}
									indeterminate={isSplit && selectedRows.length > 0}
									inputProps={{ "aria-label": "Show every meter" }}
									onChange={() =>
										onMeterChange(otherRows.length === 0 ? [] : null)
									}
									sx={{ p: 0.25 }}
								/>
							</TableCell>
							<TableCell sx={headCellSx}>Meter</TableCell>
							<TableCell sx={headCellSx}>Account</TableCell>
							<TableCell sx={headCellSx}>Last reading</TableCell>
							<TableCell align="right" sx={headCellSx}>
								Lag
							</TableCell>
							<TableCell sx={headCellSx}>Status</TableCell>
							<TableCell sx={headCellSx}>Period coverage</TableCell>
							<TableCell align="right" sx={headCellSx}>
								Period ({VOLUME_UNIT})
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{isSplit ? (
							<>
								<SectionRow label="On screen" count={selectedRows.length} />
								{selectedRows.map(renderRow)}
								<SectionRow label="Filtered out" count={otherRows.length} />
								{otherRows.map(renderRow)}
							</>
						) : (
							rows.map(renderRow)
						)}
					</TableBody>
				</Table>
			</Box>
		</Box>
	);
};
